// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Select, { Option } from 'presentations/views/components/atoms/Select';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import { DateTime, formatToShortWeekDay, getDateTime } from 'infras/datetime';
import {
  fontSize,
  mediaMaxWithLayoutTheme,
  spacing,
} from 'presentations/views/styles';
import { ScheduleFormData } from 'presentations/views/helpers/routine';
import {
  black,
  gray01,
  gray05,
  gray06,
  primary,
  white,
} from 'presentations/views/styles/color';

export interface Props {
  startedAt: DateTime | null;
  isAllday: boolean;
  schedule: ScheduleFormData[];
  index: number;
  onSubmit?: any;
  onDelete?: any;
  deletable: boolean;
  isDisabled?: boolean;
  onlyWeekly?: boolean;
  // hooks
  repeatUnit: string;
  monthInterval: number;
  day: number | null;
  dayInterval: number;
  wday: any[];
  wdayInterval: number;
  hour: number;
  minute: number;
  onChange: any;
  onChangeWday: (value: number) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const repeatUnitOptions: Option[] = props.onlyWeekly
    ? [
        {
          text: t('Weekly'),
          value: 'weekly',
        },
      ]
    : [
        { text: t('Monthly'), value: 'monthly' },
        {
          text: t('Weekly'),
          value: 'weekly',
        },
        {
          text: t('Daily'),
          value: 'daily',
        },
      ];

  const monthOptions: Option[] = useMemo(
    () =>
      Array.from({ length: 12 }).map((v, month) => ({
        text:
          month === 0 ? t('Every Month') : t('Month', { number: month + 1 }),
        value: month + 1,
      })),
    [],
  );
  const dayOptions: Option[] = useMemo(
    () =>
      Array.from({ length: 31 }).map((v, day) => ({
        text: t('Specified Day', { number: day + 1 }),
        value: day + 1,
      })),
    [],
  );
  const wdayIntervalOptions: Option[] = useMemo(
    () =>
      Array.from({
        length: 4,
      }).map((v, week) => ({
        text:
          week === 0
            ? t('Every Week')
            : t('Week', {
                number: week + 1,
              }),
        value: week + 1,
      })),
    [],
  );
  const wdayOptions = useMemo(
    () =>
      Array.from({
        length: 7,
      }).map((v, wday) => ({
        text: formatToShortWeekDay(
          getDateTime().set({
            weekday: wday,
          }),
        ),
        value: wday,
      })),
    [],
  );

  const dayIntervalOptions: Option[] = useMemo(
    () =>
      Array.from({
        length: 30,
      }).map((v, day) => ({
        text:
          day === 0
            ? t('Every Day')
            : t('Day', {
                number: day + 1,
              }),
        value: day + 1,
      })),
    [],
  );

  return (
    <Container>
      <Section>
        {props.repeatUnit === 'monthly' && (
          <Field>
            <StyledSelect
              onChange={props.onChange}
              name="monthInterval"
              options={monthOptions}
              value={props.monthInterval}
              variant="filled"
              isFullWidth
              isDisabled={props.isDisabled}
            />
          </Field>
        )}
        {props.repeatUnit === 'weekly' && (
          <Field>
            <StyledSelect
              onChange={props.onChange}
              name="wdayInterval"
              options={wdayIntervalOptions}
              value={props.wdayInterval}
              variant="filled"
              isFullWidth
              isDisabled={props.isDisabled}
            />
          </Field>
        )}
        {props.repeatUnit === 'daily' && (
          <Field>
            <StyledSelect
              onChange={props.onChange}
              name="dayInterval"
              options={dayIntervalOptions}
              value={props.dayInterval}
              variant="filled"
              isFullWidth
              isDisabled={props.isDisabled}
            />
          </Field>
        )}
        <Field>
          <StyledSelect
            onChange={props.onChange}
            name="repeatUnit"
            options={repeatUnitOptions}
            value={props.repeatUnit}
            variant="filled"
            isFullWidth
            isDisabled={props.isDisabled}
          />
        </Field>
      </Section>

      <Section>
        {props.repeatUnit === 'monthly' && (
          <Field>
            <StyledSelect
              onChange={props.onChange}
              name="day"
              options={dayOptions}
              value={props.day}
              variant="filled"
              isDisabled={props.isDisabled}
            />
          </Field>
        )}
        {props.repeatUnit === 'weekly' && (
          <Field>
            <WeekdayFieldContent>
              {wdayOptions.map((wday) => (
                <Weekday
                  key={wday.value}
                  isChecked={
                    props.wday.find((w) => w === wday.value) !== undefined
                  }
                  onClick={() => {
                    if (props.isDisabled) return;
                    props.onChangeWday(wday.value);
                  }}
                >
                  {wday.text}
                </Weekday>
              ))}
            </WeekdayFieldContent>
          </Field>
        )}
      </Section>

      {props.deletable && (
        <Section>
          <DeleteButton
            onClick={() => props.onDelete(props.index)}
            variant="text"
            color="inherit"
            isDisabled={props.isDisabled}
          >
            <Icon name="delete" size={fontSize(16)} />
          </DeleteButton>
        </Section>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    flex-direction: column;
    align-items: flex-end;
  }
`;
const Section = styled.div`
  display: flex;

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 100%;
    margin-bottom: ${spacing(4)};
  }
`;
const Field = styled.div`
  margin-right: ${spacing(4)};

  &:last-of-type {
    margin-right: 0;
  }

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    flex: 1;
  }
`;
const WeekdayFieldContent = styled.div`
  display: flex;
`;
const Weekday = styled.div<{ isChecked: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: ${spacing(4)};
  border-radius: 50%;

  ${(props) => css`
    color: ${props.isChecked ? white(props) : black(props)};
    background-color: ${props.isChecked ? primary(props) : gray06(props)};
    &:hover {
      background-color: ${props.isChecked ? primary(props) : gray05(props)};
    }
  `}

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    font-size: ${fontSize(11)};
  }

  &:last-of-type {
    margin-right: 0;
  }
`;
const StyledSelect = styled(Select)`
  .MuiFilledInput-root {
    background-color: ${gray06};
    font-size: ${fontSize(14)};
  }
  .MuiFilledInput-root:hover {
    background-color: ${gray05};
  }
  .MuiFilledInput-input {
    padding: ${spacing(8)} ${spacing(32)} ${spacing(8)} ${spacing(12)};
  }
  .MuiFilledInput-underline:before {
    border: none;
  }
  .MuiFilledInput-underline:after {
    border: none;
  }
`;
const DeleteButton = styled(Button)`
  font-size: ${fontSize(13)};
  font-weight: initial;
  min-width: 0;
  padding: ${spacing(4)};
  color: ${gray01};
`;
